/* eslint-disable no-unused-expressions */
import { useState, useCallback } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';

import { hooks as orderHooks } from '@/domains/esim/order';
import { processDeviceData } from '@/utils/filterDevicesData';

import SectionTitle from '../SectionTitle/SectionTitle';
import LinkNumbers from '../LinkNumbers/LinkNumbers';
import TextBlock from '../TextBlock/TextBlock';
import Button from '../Button/Button';
import Tabs from '../Tabs/Tabs';
import TabPanel from '../Tabs/TabPanel';
import Tab from '../Tabs/Tab';
import TabContent from '../Tabs/TabContent';
import TabContentItem from '../Tabs/TabContentItem';
import CompatibleDevicesModalEsim from '../CompatibleDevicesModal/CompatibleDevicesModal';
import CompatibleDevicesModal from '../CompatibleDevicesModalVolte/CompatibleDevicesModalVolte';
import VideoModal from '../VideoModal/VideoModal';

import styles from './StepsBanner.module.scss';

const StepsBannerWithTabs = ({ data, className }) => {
  const { t } = useTranslation();

  const [activeClass, setActiveClass] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [imageData, setImageData] = useState({
    url: data.sections[activeTab]?.steps[0]?.image,
    alt: data.sections[activeTab]?.steps[0]?.image,
  });
  const [activeModal, setActiveModal] = useState(0);
  const [activeVideoModal, setActiveVideoModal] = useState(0);
  const [deviceModal, setDeviceModal] = useState(false);

  const router = useRouter();

  const isEsim =
    router.pathname === '/esim' || router.asPath.startsWith('/esim');

  const handleChangeTab = (index) => {
    setActiveTab(index);
    setActiveClass(0);
    setImageData({
      url: data.sections[index]?.steps[0]?.image,
      alt: data.sections[index]?.steps[0]?.image,
    });
  };

  const popup = () =>
    data?.playlist_link
      ? window.open(data?.playlist_link, '_blank')
      : setActiveVideoModal(data.youtube_id);

  const setActiveScreen = useCallback(
    (index) => {
      setActiveClass(index);
      setImageData({
        url: data.sections[activeTab]?.steps[index]?.image,
        alt: data.sections[activeTab]?.steps[index]?.image,
      });
    },
    [data, activeTab],
  );

  const { data: deviceData } = orderHooks.useGetDeviceList('');
  const fullDevices = processDeviceData(deviceData, t);
  const renderSteps = (item, index) => {
    const currentActiveElement =
      activeClass === index ? styles['step-item--active'] : '';

    return (
      <div
        className={classNames(styles['step-item'], currentActiveElement)}
        key={index}
        onMouseOver={() => setActiveScreen(index)}
        itemScope
        itemType='https://schema.org/Property'
      >
        <div className={styles['step-item__count']}>
          {t('common:Step')} {`${item.title}`}
        </div>
        <div className={styles['step-item__text']}>{item.description}</div>
      </div>
    );
  };

  const renderTab = (item, index) => {
    return (
      <Tab key={index} onClick={() => handleChangeTab(index)}>
        {item.title}
      </Tab>
    );
  };

  const renderContent = (item, index) => {
    return (
      <TabContentItem key={index}>
        {item?.steps?.map(renderSteps)}
      </TabContentItem>
    );
  };
  return (
    <section
      id={data?.scroll_id}
      className={classNames(styles['step-banner'], className)}
    >
      <div className={styles['step-banner__wrapper']}>
        <div className={styles['step-banner__content']}>
          <div className={styles['step-banner__head']}>
            {data?.title && (
              <SectionTitle className={styles.step_banner_title}>
                {data.title}
              </SectionTitle>
            )}
            {data?.ussd && (
              <>
                <div className={styles['step-banner__link']}>
                  {data?.text_before_ussd}
                  {data?.text_after_ussd}
                </div>
                <LinkNumbers
                  numbers={data.ussd}
                  isTel
                  className={styles.links}
                />
              </>
            )}
            {data?.description && (
              <TextBlock
                className={classNames(
                  styles['step-banner__text'],
                  'text--medium',
                )}
              >
                {data.description}
              </TextBlock>
            )}
          </div>

          <div className={styles['step-banner__buttons']}>
            {(data?.video_button_text || data?.playlist_link) && (
              <Button onClick={popup}>{data?.video_button_text}</Button>
            )}
            <Button
              variant='outline'
              onClick={() => {
                isEsim ? setDeviceModal(true) : setActiveModal(true);
              }}
              className={styles.steps_btn}
            >
              {data?.devices_button_text}
            </Button>
          </div>
        </div>
        <div className={styles['step-banner__phone-wrapper']}>
          <div className={styles['step-banner__phone']}>
            <Image
              className={styles['step-banner__phone-screen']}
              src={imageData.url}
              alt={imageData.url}
              title={imageData.url}
              loading='lazy'
              width={248}
              height={430}
            />
          </div>
        </div>
        <div>
          <Tabs active={activeTab}>
            <TabPanel className={styles.tabs}>
              {data?.sections?.map(renderTab)}
            </TabPanel>
            <TabContent>{data?.sections?.map(renderContent)}</TabContent>
          </Tabs>
        </div>
      </div>
      {isEsim ? (
        <CompatibleDevicesModalEsim
          active={deviceModal}
          setActive={setDeviceModal}
          devices={fullDevices}
          className={styles.volte_modal_inner}
        />
      ) : (
        <CompatibleDevicesModal
          active={activeModal}
          setActive={setActiveModal}
          devices={data?.brands
            ?.filter((item) => item?.id === 2)
            ?.concat(data?.brands?.filter((item) => item?.id !== 2))}
          title={data?.devices_modal_title}
          data={data}
          className={styles.volte_modal_inner}
        />
      )}

      <VideoModal
        videoId={activeVideoModal}
        onClose={() => setActiveVideoModal(null)}
      />
    </section>
  );
};

export default StepsBannerWithTabs;
